<template>
  <!-- Pricing Toggle -->
  <div class="flex justify-center my-12">
    <div class="bg-white dark:bg-stone-800 rounded-lg p-1 inline-flex shadow-md">
      <button
        :class="[
          'px-4 py-2 rounded-md cursor-pointer',
          billingType === 'monthly' ? 'bg-blue-500 text-white' : 'text-gray-700 dark:text-gray-300',
        ]"
        @click="billingType = 'monthly'"
      >
        Monthly billing
      </button>
      <button
        id="yearly-button"
        :class="[
          'px-4 py-2 rounded-md cursor-pointer relative',
          billingType === 'yearly' ? 'bg-blue-500 text-white' : 'text-gray-700 dark:text-gray-300',
        ]"
        @click="billingType = 'yearly'"
      >
        Yearly billing
        <div class="absolute -top-8 -right-8 bg-orange-500 text-white p-2 shadow-md rounded-full font-bold">- 25%</div>
      </button>
    </div>
  </div>

  <!-- Pricing Cards -->
  <div
    class="pricing-cards max-w-7xl mx-auto grid grid-cols-1 gap-8"
    :class="`${full ? 'md:grid-cols-3' : 'md:grid-cols-2'}`"
  >
    <!-- Free Plan -->
    <section
      v-if="full"
      class="bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-gray-900 overflow-hidden relative border-2 border-gray-200 dark:border-gray-700"
    >
      <div class="px-6 pt-8 pb-18">
        <div class="flex gap-2 items-center">
          <img src="@/images/icons/plan_free.png" alt="PicEditor Free Plan" class="w-12" />
          <h3 class="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">Free</h3>
        </div>
        <div class="mt-4">
          <span class="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white">$0</span>
        </div>
        <div class="text-lg font-bold mt-12 text-gray-800 dark:text-gray-300">Include:</div>
        <ul class="mt-6 space-y-4 text-gray-800 dark:text-gray-300">
          <li
            v-for="(item, i) in [
              'Access to all features',
              'No watermark',
              'File size up to 4 MB',
              'Face swap, Upscale and restore image, Colorize black and white photo, Remove background, Blur background:\nImage dimension up to 480x480',
            ]"
            :key="i"
            class="flex items-center gap-2"
          >
            <i class="fa-solid fa-check text-green-600 min-w-6"></i>
            <span class="whitespace-pre-line">{{ item }}</span>
          </li>
          <li v-for="(item, i) in ['Bulk process', 'No ads', 'Mail support']" :key="`x-${i}`" class="flex items-center">
            <i class="fa-solid fa-xmark text-rose-600 w-6"></i>
            <span>{{ item }}</span>
          </li>
        </ul>
      </div>
      <div class="px-6 py-4 bg-gray-50 dark:bg-gray-900 absolute bottom-0 w-full">
        <button
          id="free-plan-button"
          :disabled="subscriptionStore.subscription?.plan_slug === 'free' || !subscriptionStore.isActivePaidUser"
          class="w-full py-2 px-4 bg-blue-500 text-white rounded-md hover:bg-blue-600 cursor-pointer disabled:opacity-50"
          @click="subscriptionFreePlan"
        >
          {{ freePlanButtonText }}
        </button>
      </div>
    </section>

    <!-- Pro Plan -->
    <section
      class="bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-gray-900 overflow-hidden border-2 relative"
      :class="`${isCurrentPlan('pro', billingType) || isSelectedPlan('pro', billingType) ? 'border-blue-500' : 'border-gray-200 dark:border-gray-700'}`"
    >
      <div
        v-if="isCurrentPlan('pro', billingType)"
        class="absolute top-0 right-0 bg-blue-500 text-white px-3 py-1 rounded-bl-lg"
      >
        Current plan
      </div>
      <div
        v-else-if="isSelectedPlan('pro', billingType)"
        class="absolute top-0 right-0 bg-green-500 text-white px-3 py-1 rounded-bl-lg"
      >
        Selected plan
      </div>
      <div class="px-6 pt-8 pb-20">
        <div class="flex gap-2 items-center">
          <img src="@/images/icons/plan_pro.png" alt="PicEditor Pro Plan" class="w-12" />
          <h3 class="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">Pro</h3>
        </div>
        <div class="mt-4 text-gray-900 dark:text-white">
          <span class="text-4xl lg:text-5xl font-bold">${{ billingType === 'monthly' ? '9.9' : '7.4' }}</span>
          <span class="text-gray-500 dark:text-gray-300">/month</span>
          <div class="mt-2 text-lg text-orange-700 dark:text-orange-400">
            {{ billingType === 'monthly' ? 'Billed monthly' : '$89 Billed annually' }}
          </div>
        </div>
        <div class="text-lg font-bold mt-4 text-green-600">500 credits/month (up to 500 images)</div>
        <ul v-if="full" class="mt-6 space-y-4 text-gray-800 dark:text-gray-300">
          <li
            v-for="(item, i) in [
              'Access to all features',
              'Unused credits roll over',
              'No watermark',
              'File size up to 20 MB',
              'Face swap, Upscale and restore image, Colorize black and white photo, Remove background, Blur background:\nImage dimension up to 12000x12000',
              'Bulk process',
              'No ads',
              'Mail support',
              'Upscale 4x, face swap NSFW, remove background high quality, and more.',
            ]"
            :key="i"
            class="flex items-center gap-2"
          >
            <i class="fa-solid fa-check text-green-600 min-w-6"></i>
            <span class="whitespace-pre-line">{{ item }}</span>
          </li>
        </ul>
      </div>
      <div class="px-6 py-4 bg-gray-50 dark:bg-gray-900 absolute bottom-0 w-full">
        <button
          id="pro-plan-button"
          :disabled="
            subscriptionStore.isActivePaidUser &&
            subscriptionStore.subscription?.plan_slug === 'pro' &&
            subscriptionStore.subscription?.billing_type === billingType
          "
          class="w-full py-2 px-4 bg-blue-500 text-white rounded-md hover:bg-blue-600 cursor-pointer disabled:opacity-50"
          @click="subscriptionProPlan"
        >
          {{ proPlanButtonText }}
        </button>
      </div>
    </section>

    <!-- Enterprise Plan -->
    <section
      class="bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-gray-900 overflow-hidden border-2 relative"
      :class="`${isCurrentPlan('enterprise', billingType) || isSelectedPlan('enterprise', billingType) ? 'border-blue-500' : 'border-gray-200 dark:border-gray-700'}`"
    >
      <div
        v-if="isCurrentPlan('enterprise', billingType)"
        class="absolute top-0 right-0 bg-blue-500 text-white px-3 py-1 rounded-bl-lg"
      >
        Current plan
      </div>
      <div
        v-else-if="isSelectedPlan('enterprise', billingType)"
        class="absolute top-0 right-0 bg-green-500 text-white px-3 py-1 rounded-bl-lg"
      >
        Selected plan
      </div>
      <div class="px-6 pt-8 pb-20">
        <div class="flex gap-2 items-center">
          <img src="@/images/icons/plan_enterprise.png" alt="PicEditor Enterprise Plan" class="w-12" />
          <h3 class="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">Enterprise</h3>
        </div>
        <div class="mt-4 text-gray-900 dark:text-white">
          <span class="text-4xl lg:text-5xl font-bold">${{ billingType === 'monthly' ? '29.9' : '22.4' }}</span>
          <span class="text-gray-500 dark:text-gray-300">/month</span>
          <div class="mt-2 text-lg text-orange-700 dark:text-orange-400">
            {{ billingType === 'monthly' ? 'Billed monthly' : '$269 Billed annually' }}
          </div>
        </div>
        <div class="text-lg font-bold mt-4 text-green-600">2000 credits/month (up to 2000 images)</div>
        <ul v-if="full" class="mt-6 space-y-4 text-gray-800 dark:text-gray-300">
          <li class="flex items-center">
            <i class="fa-solid fa-check text-green-600 min-w-6"></i>
            <span
              class="font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text dark:from-amber-400 dark:to-pink-400 text-transparent drop-shadow-md"
            >
              Priority server, faster
            </span>
          </li>
          <li
            v-for="(item, i) in [
              'Unused credits roll over',
              'No watermark',
              'File size up to 40 MB',
              'Face swap, Upscale and restore image, Colorize black and white photo, Remove background, Blur background:\nImage dimension up to 12000x12000',
              'High demand bulk process',
              'No ads',
              'Priority mail support',
              'Upscale 4x, face swap NSFW, remove background high quality, and more.',
            ]"
            :key="i"
            class="flex items-center gap-2"
          >
            <i class="fa-solid fa-check text-green-600 min-w-6"></i>
            <span class="whitespace-pre-line">{{ item }}</span>
          </li>
        </ul>
      </div>
      <div class="px-6 py-4 bg-gray-50 dark:bg-gray-900 absolute bottom-0 w-full">
        <button
          id="enterprise-plan-button"
          :disabled="
            subscriptionStore.isActivePaidUser &&
            subscriptionStore.subscription?.plan_slug === 'enterprise' &&
            subscriptionStore.subscription?.billing_type === billingType
          "
          class="w-full py-2 px-4 bg-blue-500 text-white rounded-md hover:bg-blue-600 cursor-pointer disabled:opacity-50"
          @click="subscriptionEnterprisePlan"
        >
          {{ enterprisePlanButtonText }}
        </button>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
  withDefaults(
    defineProps<{
      full?: boolean
    }>(),
    {
      full: false,
    },
  )

  import { ref, computed, onMounted } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { useStore } from '$/stores/index.js'
  import { useSessionStore } from '$/stores/session.js'
  import { useSubscriptionStore } from '$/stores/subscription.ts'

  const route = useRoute()
  const router = useRouter()
  const store = useStore()
  const sessionStore = useSessionStore()
  const subscriptionStore = useSubscriptionStore()
  const billingType = ref('monthly')

  onMounted(() => {
    billingType.value = route.query.billing_type === 'yearly' ? 'yearly' : 'monthly'
  })

  const isCurrentPlan = function (plan, billingType) {
    return (
      subscriptionStore.subscription.plan_slug === plan && subscriptionStore.subscription.billing_type === billingType
    )
  }

  const isSelectedPlan = function (plan, billingType) {
    return route.query.plan === plan && route.query.billing_type === billingType
  }

  const freePlanButtonText = computed(() => {
    if (sessionStore.user) {
      if (subscriptionStore.isActivePaidUser) return 'Switch to Free plan'
      else return 'Current plan'
    }

    return 'Try it Free'
  })

  const proPlanButtonText = computed(() => {
    if (
      !sessionStore.user ||
      subscriptionStore.subscription.plan_slug === 'free' ||
      !subscriptionStore.isActivePaidUser
    )
      return 'Go Pro'

    if (sessionStore.user) {
      if (
        subscriptionStore.subscription.plan_slug === 'pro' &&
        subscriptionStore.subscription.billing_type === billingType.value
      ) {
        return 'Current plan'
      }
    }

    return `Switch to Pro ${billingType.value} plan`
  })

  const enterprisePlanButtonText = computed(() => {
    if (!subscriptionStore.isActivePaidUser) return 'Go Enterprise'
    if (sessionStore.user) {
      if (subscriptionStore.subscription.plan_slug === 'enterprise') {
        if (subscriptionStore.subscription.billing_type === billingType.value) {
          return 'Current plan'
        } else {
          return `Switch to Enterprise ${billingType.value} plan`
        }
      }
    }

    return 'Go Enterprise'
  })

  const checkoutLinkQuery = `?checkout[email]=${sessionStore.user?.email}&checkout[name]=${sessionStore.user?.name}&checkout[custom][user_id]=${sessionStore.user?.id}`

  const subscriptionFreePlan = function () {
    if (sessionStore.user) {
      router.push({ name: 'PageChangePlan', query: { plan: 'free' } })
    } else {
      router.push({ name: 'SignUp' })
    }
  }

  const subscriptionProPlan = function () {
    if (sessionStore.user) {
      if (subscriptionStore.isActivePaidUser) {
        router.push({ name: 'PageChangePlan', query: { plan: 'pro', billing_type: billingType.value } })
        return
      }
      let checkoutLink = store.systemConfigs.pro_plan_monthly_checkout_link
      if (billingType.value === 'yearly') checkoutLink = store.systemConfigs.pro_plan_yearly_checkout_link
      window.location.href = checkoutLink + checkoutLinkQuery
    } else {
      router.push({ name: 'SignUp' })
    }
  }

  const subscriptionEnterprisePlan = function () {
    if (sessionStore.user) {
      if (subscriptionStore.isActivePaidUser) {
        router.push({ name: 'PageChangePlan', query: { plan: 'enterprise', billing_type: billingType.value } })
        return
      }
      let checkoutLink = store.systemConfigs.enterprise_plan_monthly_checkout_link
      if (billingType.value === 'yearly') checkoutLink = store.systemConfigs.enterprise_plan_yearly_checkout_link
      window.location.href = checkoutLink + checkoutLinkQuery
    } else {
      router.push({ name: 'SignUp' })
    }
  }
</script>
